import { makeAutoObservable } from "mobx";
import {
  createExtension,
  loadExtension,
  updateExtension,
  zExtensionWrite,
} from "src/business/extensions/apiExtensions";
import {
  ZExtension,
  zExtensionCommon,
} from "src/business/extensions/ZExtension";
import { mdmBreadcrumbs } from "src/common/mdmBreadcrumbs";
import { onError } from "src/common/onError";
import { RemoteData } from "src/common/RemoteData";
import z from "zod";

const zExtensionFormValues = zExtensionCommon
  .partial()
  .describe("Форма расширения");

export type ZExtensionFormValues = z.infer<typeof zExtensionFormValues>;

export type ExtensionTabId = "main" | "sys";

type ExtensionData = {
  extension: ZExtension | null;
};

export const extensionCardPageStore = makeAutoObservable({
  data: { status: "none" } as RemoteData<ExtensionData>,
  setData(newData: RemoteData<ExtensionData>) {
    this.data = newData;
  },

  saving: false,
  setSaving(value: boolean) {
    this.saving = value;
  },

  async init(extensionId: string) {
    try {
      this.setCurTab("main");
      this.setData({ status: "wait" });
      const extension = !extensionId ? null : await loadExtension(extensionId);
      this.setData({ status: "ready", result: { extension } });
    } catch (error) {
      this.setData({ status: "error", error });
    }
  },

  get isNew() {
    const { data } = this;
    if (data.status !== "ready") return false;
    return !data.result.extension;
  },

  get extensionId() {
    const { data } = this;
    if (data.status !== "ready") return "";
    return data.result.extension?.id ?? "";
  },

  get title() {
    const { isNew, data } = this;
    if (isNew) return "Новое расширение";
    return data.status === "ready" ? data.result.extension.name : "...";
  },
  get crumbs() {
    return mdmBreadcrumbs([
      { type: "AdminExtensions" },
      { type: "Name", name: this.title },
    ]);
  },

  async save(
    values: ZExtensionFormValues,
    onSuccess: (extension: ZExtension) => void,
  ) {
    try {
      this.setSaving(true);
      const formValues = zExtensionFormValues.parse(values);
      const { file, ...fields } = formValues;
      const body = zExtensionWrite.parse({ ...fields, fileId: file?.id });
      const extension = this.isNew
        ? await createExtension(body)
        : await updateExtension(this.extensionId, body);
      this.setData({
        status: "ready",
        result: { extension },
      });
      onSuccess(extension);
    } catch (error) {
      onError(error);
    } finally {
      this.setSaving(false);
    }
  },
  curTab: "main" as ExtensionTabId,
  setCurTab(tab: ExtensionTabId) {
    this.curTab = tab;
  },
  get tabOptions() {
    const res: { key: ExtensionTabId; label: string }[] = [
      { key: "main", label: "Общее" },
    ];
    if (!this.isNew) {
      res.push({ key: "sys", label: "Системные данные" });
    }
    return res;
  },
});
